/**
 * JOSSE-COLA-T1 — LO QUE NECESITA DE VOS.
 *
 * AUDIENCIA (regla #5638): JOSSE, dueno, en el panel de escritorio. Es la
 * pantalla donde ve lo que SOLO EL puede destrabar, y lo destraba ahi mismo.
 * No es la tablet del colaborador, no es el home del negocio, y sobre todo NO
 * es un tablero de estado: un tablero dice como va todo; esta cola dice una
 * sola cosa -- que esta parado esperandolo a el.
 *
 * DE DONDE SALE, y Ares lo nombro primero como problema suyo (#10825):
 *
 *   "Josse me escribio que le dejo documentacion, preguntas y necesidades en el
 *    chat y que se le diluyen, porque sigo y sigo copiando. Tiene razon, y es
 *    peor de lo que suena: hoy le mande NUEVE mensajes y le deje SIETE
 *    decisiones abiertas."
 *
 * LAS CUATRO COSAS QUE LA HACEN DISTINTA DE UN TABLERO MAS, y son de Ares:
 *   1. Muestra TRES, no todas. Una lista de dieciocho es otra forma de diluir.
 *   2. Dice QUE PASA SI NO DECIDE. Sin costo, no hay razon para decidir hoy.
 *   3. MARCA LO VISTO -- y ademas avisa si CAMBIO desde que lo viste, que es la
 *      queja original: "un archivo que yo iba modificando y el tenia que releer
 *      sin saber que habia cambiado".
 *   4. Puede decidir AHI. La decision se publica como [DECISION-JOSSE] y el
 *      agente parado la levanta en su loop. Sin eso la cola es un tablero de
 *      quejas con otro nombre.
 *
 * Y AQUI ME CORRIJO, porque lo que decia esta linea era falso. Yo escribi que
 * esta pantalla NO podia ordenar por lo que cuesta no decidir, "porque solo 3
 * de 18 traen ese dato". Medi el nombre que YO habia elegido, no los que hay:
 *
 *   costo_nivel .......... alto / medio / bajo. SIETE de dieciocho.
 *   costo_de_no_decidir .. que pasa si no decide. Siete, mas mis tres = OCHO.
 *   cuanto_le_cuesta ..... cuanto trabajo le cuesta A EL. SIETE.
 *
 * Con mis nombres leia 3 de 18. Con los reales, 8. Y costo_nivel permite
 * ordenar por costo de verdad en las que lo traen. Es, otra vez, medir la forma
 * que uno espera en vez de la capacidad que busca.
 *
 * ORDENA POR COSTO donde esta escrito, por prioridad y antiguedad en el resto,
 * y DICE CUANTAS son de cada clase: un orden a medias presentado como completo
 * hace creer que lo de arriba es lo mas urgente.
 */
import { JosseColaApiClient } from '../../infrastructure/api/JosseColaApiClient.js';
import { AppConfig } from '../../config.js';
import { resolveUserAudience } from '../shared/useAudience.js';
import { leerDecision, ordenar, filtrar, contar, estadoDe, quienesPidieron, nombreDe } from '../../application/decisiones/tableroDecisiones.js';

const C = {
  texto: '#e8e8f0',
  suave: '#8a8aa8',
  linea: '#2a2a3e',
  bien: '#2f6b40',
  ojo: 'var(--accent-yellow)',
  mal: '#ff9a9a',
  acento: '#7fe8d8'
};

const CUANTAS = 3;                       // TRES, no todas: el numero es de Ares
const LLAVE_VISTAS = 'nx_josse_cola_vistas';

function puedeVer() {
  const rol = AppConfig.role;
  const aud = resolveUserAudience();
  return ['dev', 'admin'].includes(rol) && (aud === 'all' || aud === 'nexo');
}

// Deuda 4: el escape local no cambiaba la comilla simple; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

/**
 * LO VISTO SE GUARDA CON SU VERSION, no con un si/no.
 *
 * Guardar "ya la vi" no resuelve la queja de Josse: el problema era releer algo
 * que habia cambiado sin saber que habia cambiado. Guardando el updatedAt que
 * tenia CUANDO la vio, la pantalla puede decir "esto cambio desde que lo viste".
 *
 * Vive en el navegador, y eso tambien se dice: si abre el panel en otro equipo,
 * las marcas no viajan. Es una comodidad, no un registro. Poner esto en la BD
 * seria mejor y necesita un campo que hoy no existe.
 */
function vistas() {
  try { return JSON.parse(localStorage.getItem(LLAVE_VISTAS) || '{}') || {}; }
  catch { return {}; }
}
function marcarVista(id, version) {
  try {
    const v = vistas();
    v[id] = version || '1';
    localStorage.setItem(LLAVE_VISTAS, JSON.stringify(v));
  } catch { /* modo privado o almacenamiento lleno: la marca se pierde, la cola sigue */ }
}

function edad(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const dias = Math.floor((Date.now() - t) / 86400000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'desde ayer';
  return `hace ${dias} dias`;
}

function pantallaSinDatos(motivo, detalle) {
  const titulos = {
    SIN_SESION: 'Tu sesion no alcanza para ver esto',
    PROHIBIDO: 'Esta pantalla no es para tu usuario',
    NO_PUBLICADO: 'Esta API todavia no publica las tareas',
    DEV_SIN_API: 'Estas en el panel de desarrollo',
    SIN_RED: 'No pudimos hablar con la API',
    ERROR_API: 'La API respondio con un error'
  };
  return '<div style="padding:26px;text-align:center;border:1px solid ' + C.linea + ';border-radius:8px">'
    + '<div style="font-size:1.5rem;margin-bottom:8px">&#128220;</div>'
    + '<div style="color:' + C.texto + ';font-size:.9rem;margin-bottom:6px">' + esc(titulos[motivo] || 'No se pudo cargar') + '</div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem">' + esc(detalle || '') + '</div></div>';
}

// ============================================================================
// EL TABLERO DE DECISIONES (DECISIONES-ARTEFACTO-T1, Ares #25843, pedido de Josse en T30)
// ============================================================================
// Recientes primero con las pendientes arriba, filtro por estado y por quien pidio, y cada
// tarjeta con pregunta, opciones (la elegida marcada), respuesta, fecha, quien respondio y sus
// vinculos. El orden, los filtros y la cuenta viven en application/decisiones/tableroDecisiones.js.
const DECISIONES_POR_TANDA = 15;
const ESTADO_UI = {
  pendiente: { txt: 'Pendiente', color: 'var(--accent-yellow-light)' },
  respondida: { txt: 'Respondida', color: 'var(--accent-blue)' },
  implementada: { txt: 'Implementada', color: 'var(--accent-green-text)' }
};

function fechaCorta(iso) {
  if (!iso) return '';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '';
  try {
    return formatearFecha(t, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' }, { parte: 'todo' });
  } catch { return t.toISOString().slice(0, 16).replace('T', ' '); }
}

const chip = (txt) => '<span style="display:inline-block;font-size:0.875rem;color:' + C.suave + ';border:1px solid ' + C.linea
  + ';border-radius:10px;padding:1px 8px;margin:2px 4px 0 0">' + txt + '</span>';

function tarjetaDecisionHtml(d) {
  const e = estadoDe(d);
  const ui = ESTADO_UI[e.estado];
  const opciones = d.opciones.length
    ? '<ul style="margin:6px 0 0;padding-left:18px">' + d.opciones.map((o) => {
      const elegida = d.respuestaClave && o.clave && o.clave.toLowerCase() === d.respuestaClave.toLowerCase();
      return '<li style="font-size:0.875rem;line-height:1.4;margin-bottom:2px;color:' + (elegida ? C.texto : C.suave) + '"'
        + (elegida ? ' data-dec-elegida' : '') + '>'
        + (elegida ? '<strong style="color:var(--accent-green-text)">&#10003; </strong>' : '')
        + (o.clave ? '<b>' + esc(o.clave) + ')</b> ' : '') + esc(o.texto)
        + (o.recomendada ? ' <span style="color:' + C.acento + '">(recomendada)</span>' : '') + '</li>';
    }).join('') + '</ul>'
    : '';
  // PROCEDENCIA (Ares #25944): quien respondio, cuando y por que canal. Y una pendiente con respuesta
  // escrita es una respuesta PARCIAL (D-105, T38): se muestra como tal, no se esconde por estar pendiente.
  const procedencia = '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:3px" data-dec-procedencia>'
    + (d.respondidaPor ? 'respondio ' + esc(d.respondidaPor) : 'no dice quien respondio')
    + (d.respondidaEl ? ' &middot; ' + esc(fechaCorta(d.respondidaEl)) : '')
    + (d.canal ? ' &middot; por ' + esc(d.canal) : '') + '</div>';
  const respuesta = d.estadoBase === 'pendiente'
    ? (d.respuesta
      ? '<div style="font-size:0.875rem;color:' + C.texto + ';margin-top:8px;line-height:1.4" data-dec-parcial><b style="color:var(--accent-yellow-light)">Respuesta parcial:</b> ' + esc(d.respuesta) + '</div>'
        + (d.canal ? '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:3px">por ' + esc(d.canal) + '</div>' : '')
      : '')
      + (d.vencida ? '<div style="font-size:0.875rem;color:var(--accent-red);margin-top:6px">Se le paso la fecha y nadie contesto.</div>' : '')
    : '<div style="font-size:0.875rem;color:' + C.texto + ';margin-top:8px;line-height:1.4"><b>Respuesta:</b> ' + esc(d.respuesta || '(sin texto)') + '</div>'
      + procedencia;
  const impl = {
    medida: e.estado === 'implementada'
      ? 'la tarea ligada esta completed'
      : 'la tarea ligada sigue en ' + esc(d.tareaEstado || '?'),
    sin_medir: 'implementacion sin medir: la API aun no trae el estado de la tarea ligada',
    sin_tarea: 'sin tarea ligada: no hay como saber si se implemento'
  }[e.implementacion];
  const vinculos = [
    d.mision ? chip('mision ' + esc(d.mision)) : '',
    d.tarea ? chip('tarea ' + esc(d.tarea)) : '',
    d.bitacoraId ? chip('bitacora #' + esc(d.bitacoraId)) : ''
  ].join('');
  return '<div data-dec-codigo="' + esc(d.codigo) + '" data-dec-estado-ui="' + e.estado + '" style="background:var(--bg-canvas);border:1px solid ' + C.linea
    + ';border-left:3px solid ' + ui.color + ';border-radius:8px;padding:10px 12px;margin-bottom:8px">'
    + '<div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:baseline">'
    + '<div style="color:' + C.texto + ';font-size:0.875rem;line-height:1.35;flex:1 1 220px;min-width:0;overflow-wrap:anywhere">'
    + '<code style="font-size:0.875rem;color:' + C.suave + '">' + esc(d.codigo) + '</code> ' + esc(d.titulo) + '</div>'
    + '<span style="font-size:0.875rem;color:' + ui.color + ';white-space:nowrap">' + ui.txt + '</span></div>'
    + (d.pregunta ? '<div style="font-size:0.875rem;color:' + C.texto + ';margin-top:6px;line-height:1.4;opacity:.9">' + esc(d.pregunta) + '</div>' : '')
    + opciones + respuesta
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">la pidio ' + esc(nombreDe(d.pedidaPor))
    + (d.creadaEl ? ' &middot; ' + esc(fechaCorta(d.creadaEl)) : '') + (impl ? ' &middot; ' + impl : '') + '</div>'
    + (vinculos ? '<div style="margin-top:4px">' + vinculos + '</div>' : '')
    + '</div>';
}

export function tableroHtml(todas, filtro, cuantasVer, truncado) {
  const c = contar(todas);
  const lista = filtrar(todas, filtro);
  const visibles = lista.slice(0, cuantasVer);
  const boton = (clave, txt, n) => {
    const activo = filtro.estado === clave;
    return '<button type="button" data-dec-estado="' + clave + '" aria-pressed="' + activo + '" style="padding:5px 10px;border-radius:14px;font-size:0.875rem;cursor:pointer;'
      + 'border:1px solid ' + (activo ? 'var(--accent-blue-strong)' : C.linea) + ';background:' + (activo ? '#1f6feb33' : 'var(--bg-tertiary)') + ';color:' + C.texto + '">'
      + txt + ' <b>' + n + '</b></button>';
  };
  const quienes = quienesPidieron(todas);
  return '<div style="margin-bottom:18px" data-dec-tablero>'
    + '<div style="font-size:0.875rem;color:' + C.texto + ';font-weight:600;margin-bottom:4px">Decisiones '
    + '<span style="font-size:0.875rem;font-weight:400;color:' + C.suave + '">(la tabla de decisiones, donde quedan escritas)</span></div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-bottom:8px">'
    + '<strong style="color:var(--accent-yellow-light)" data-dec-pendientes>' + c.pendiente + '</strong> esperan tu respuesta &middot; '
    + c.total + ' en total. Pendientes arriba, el resto de la mas reciente a la mas vieja.</div>'
    + '<div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-bottom:10px">'
    + boton('todas', 'Todas', c.total) + boton('pendiente', 'Pendientes', c.pendiente)
    + boton('respondida', 'Respondidas', c.respondida) + boton('implementada', 'Implementadas', c.implementada)
    + '<label style="font-size:0.875rem;color:' + C.suave + ';margin-left:4px" for="dec-quien">Pidio</label>'
    + '<select id="dec-quien" data-dec-quien style="padding:4px 6px;border-radius:6px;border:1px solid ' + C.linea + ';background:var(--bg-canvas);color:' + C.texto + ';font-size:0.875rem">'
    + '<option value="">todos</option>'
    + quienes.map((q) => '<option value="' + esc(q.codigo) + '"' + (filtro.pedidaPor === q.codigo ? ' selected' : '') + '>' + esc(q.nombre) + ' (' + q.n + ')</option>').join('')
    + '</select></div>'
    + (c.sinMedir ? '<div style="font-size:0.875rem;color:' + C.suave + ';margin-bottom:8px">' + c.sinMedir
      + ' respondidas tienen tarea pero la API aun no dice si esa tarea se cerro: por eso "Implementadas" puede quedarse corto.</div>' : '')
    + (visibles.length
      ? visibles.map(tarjetaDecisionHtml).join('')
      : '<div style="font-size:0.875rem;color:' + C.suave + ';padding:12px;border:1px dashed ' + C.linea + ';border-radius:8px">Ninguna decision con ese filtro.</div>')
    + (lista.length > visibles.length
      ? '<button type="button" data-dec-mas style="padding:6px 12px;border-radius:6px;border:1px solid ' + C.linea + ';background:var(--bg-tertiary);color:' + C.texto
        + ';font-size:0.875rem;cursor:pointer">Ver ' + Math.min(DECISIONES_POR_TANDA, lista.length - visibles.length) + ' mas (quedan ' + (lista.length - visibles.length) + ')</button>'
      : '')
    + (truncado ? '<div style="font-size:0.875rem;color:' + C.ojo + ';margin-top:6px">La API devolvio el maximo (200): puede haber decisiones mas viejas que no se ven.</div>' : '')
    + '</div>';
}

/** Una decision esperando. */
function tarjetaHtml(f, marca) {
  const cambio = marca !== undefined && marca !== f.actualizada;
  const sello = marca === undefined
    ? ''
    : (cambio
      ? '<span style="font-size:0.875rem;color:' + C.ojo + '">&#9679; cambio desde que la viste</span>'
      : '<span style="font-size:0.875rem;color:' + C.suave + '">&#10003; ya la viste</span>');

  // LA PREGUNTA. Si nadie la escribio, se dice -- no se deja el renglon vacio,
  // que se leeria como un fallo de la pantalla en vez de un dato que falta.
  const pregunta = f.pregunta
    ? '<div style="font-size:0.875rem;color:' + C.texto + ';margin-top:8px">' + esc(f.pregunta) + '</div>'
    : '<div style="font-size:0.875rem;color:' + C.ojo + ';margin-top:8px">'
      + 'Esta tarea te espera, pero <strong>nadie escribio que te pregunta</strong>. '
      + 'Quien la abrio tiene que ponerlo antes de que puedas decidir.</div>';

  // EL COSTO. Igual: si no esta escrito, se dice que no esta.
  const costo = f.costo
    ? '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px">'
      + '<strong style="color:' + C.mal + '">Si no lo decidis:</strong> ' + esc(f.costo) + '</div>'
    : '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:6px;opacity:.85">'
      + 'No esta escrito que cuesta no decidirlo.</div>';

  // CUANTO LE CUESTA A EL DECIDIRLO, y es el campo que mas sirve de los tres.
  //
  // Los otros dos dicen que se rompe si no decide; este dice cuanto trabajo le
  // toma decidirlo -- "elegir entre dos caminos ya escritos", "elegir entre
  // tres opciones ya medidas" --. Para alguien desbordado, saber que algo le
  // toma treinta segundos es lo que hace que lo haga AHORA. Un tablero que solo
  // enumera peligros cansa; uno que ademas dice "esto es cortito" se vacia.
  //
  // Casi lo pierdo: la instruccion era fundir cuatro nombres en uno, y este
  // habria desaparecido dentro del mismo renglon que el peligro.
  const esfuerzo = f.esfuerzo
    ? '<div style="font-size:0.875rem;color:' + C.acento + ';margin-top:4px">'
      + '&#9201; Te cuesta: ' + esc(f.esfuerzo) + '</div>'
    : '';

  const e = edad(f.actualizada);
  return '<div data-cola-fila="' + esc(f.id) + '" data-version="' + esc(f.actualizada) + '" '
    + 'style="border:1px solid ' + C.linea + ';border-radius:8px;padding:12px 14px;margin-bottom:10px">'
    + '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">'
    + '<div style="min-width:220px;flex:1">'
    + '<div style="font-size:.9rem;color:' + C.texto + '">' + esc(f.titulo) + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:3px">'
    + (f.codigo ? esc(f.codigo) + ' &middot; ' : '')
    // El nivel de costo va PRIMERO en la linea: es lo que ordena la cola, asi
    // que tiene que poder leerse sin buscarlo. Si no esta escrito, no se pinta
    // nada -- un "sin costo" inventado seria peor que el hueco.
    + (f.costoNivel ? 'costo ' + esc(f.costoNivel) + ' &middot; ' : '')
    + 'prioridad ' + esc(f.prioridad || 'sin prioridad')
    + (f.dueno ? ' &middot; lo pidio ' + esc(f.dueno) : '')
    + (e ? ' &middot; ' + esc(e) : '') + '</div></div>'
    + '<div>' + sello + '</div></div>'
    + pregunta + costo + esfuerzo
    + '<div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">'
    + '<input data-cola-decision placeholder="que decidis" '
    + 'style="flex:1 1 260px;box-sizing:border-box;padding:6px 8px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:var(--bg-canvas);color:' + C.texto + ';font-size:0.875rem">'
    + '<button data-cola-publicar style="padding:6px 12px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:var(--accent-blue-strong);color:var(--text-on-accent);font-size:0.875rem;cursor:pointer">Publicar decision</button>'
    + '<button data-cola-visto style="padding:6px 10px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:var(--bg-tertiary);color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Marcar vista</button>'
    + '</div>'
    + '<div data-cola-aviso style="font-size:0.875rem;min-height:16px;margin-top:6px"></div>'
    + '</div>';
}

export async function renderJosseCola(container) {
  const $el = container;

  if (!puedeVer()) {
    $el.innerHTML = '<div class="detail-section">'
      + regresoHtml()
      + '<h1 style="margin:0;font-size:1.1rem;color:' + C.texto + '">&#128220; Lo que necesita de vos</h1>'
      + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:10px">'
      + 'Esta pantalla es del dueno del contexto y de los devs. Tu usuario entra como <strong>'
      + esc(AppConfig.role) + '</strong>.</p></div>';
    return;
  }

  $el.innerHTML = '<div class="detail-section">'
    + regresoHtml()
    + '<h1 style="margin:0;font-size:1.1rem;color:' + C.texto + '">&#128220; Lo que necesita de vos</h1>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Lo que esta <strong>parado</strong> esperando una decision tuya. No es un tablero de como va todo: '
    + 'es lo que solo vos podes destrabar, y se destraba aqui mismo.</p>'
    + '<div data-cola-cuerpo style="margin-top:14px">'
    + '<div style="padding:30px;text-align:center;color:' + C.suave + ';font-size:0.875rem">Buscando lo que te espera&hellip;</div>'
    + '</div></div>';

  const $cuerpo = $el.querySelector('[data-cola-cuerpo]');

  // DOS FUENTES, Y SE PIDEN LAS DOS (BUZON-JOSSE-T1).
  //
  //   el BUZON      la tabla decisiones_josse, que es la casa permanente que
  //                 pidio Ares: "ninguna decision de Josse vive solo en el
  //                 chat: fila en el buzon o no existe";
  //   las TAREAS    lo que esta colgado de una tarea con gate=josse, que es de
  //                 donde salia esta pantalla hasta hoy.
  //
  // NO SE SUMAN EN UN SOLO NUMERO. Son dos cosas distintas y juntarlas daria un
  // total con dos definiciones, que es como un tablero pierde la confianza. Van
  // en dos bloques, cada uno con su cuenta y su origen escrito.
  //
  // Y SI UNA SE CAE, LA OTRA SE SIGUE VIENDO. La version anterior se rendia
  // entera al primer fallo; con dos fuentes eso significaria que un permiso que
  // falta borra de la pantalla lo que si se puede leer.
  // DECISIONES-ARTEFACTO-T1 (Ares #25843): el buzon se pide ENTERO (pendientes y respondidas)
  // y se pinta como tablero: recientes primero, filtros y la tarjeta completa.
  const [res, buz] = await Promise.all([
    JosseColaApiClient.pendientes(),
    JosseColaApiClient.todas()
  ]);
  const decisiones = buz.ok ? ordenar(buz.filas.map(leerDecision)) : [];
  const filtro = { estado: 'todas', pedidaPor: '' };
  let cuantasVer = DECISIONES_POR_TANDA;

  if (!res.ok && !buz.ok) {
    $cuerpo.innerHTML = pantallaSinDatos(res.motivo, res.detalle);
    return;
  }

  let mostrarTodas = false;

  /**
   * EL BUZON, arriba y con su origen escrito.
   *
   * Falla POR CAUSA: "no tienes permiso" y "no hay decisiones" se ven igual si
   * uno no los separa, y el segundo es una buena noticia falsa: nadie investiga
   * una buena noticia.
   *
   * Para la sesion de Josse esto nace vivo: A midio (#13308) que una sesion de
   * panel con rol admin pasa aunque no traiga ningun scope. El 403 que este
   * bloque sabe contar es para las demas.
   */
  const pintarBuzon = () => {
    if (!buz.ok) {
      return '<div style="border:1px solid ' + C.linea + ';border-radius:8px;padding:12px 14px;margin-bottom:14px">'
        + '<div style="color:' + C.texto + ';font-size:0.875rem;font-weight:600">El buzon</div>'
        + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:5px;line-height:1.45">'
        + esc(buz.detalle || 'no se pudo leer') + '.<br>'
        + 'Esto NO quiere decir que no haya decisiones: quiere decir que no se pudieron leer. '
        + 'Lo de abajo sale de las tareas y si se leyo.</div></div>';
    }
    if (decisiones.length) return tableroHtml(decisiones, filtro, cuantasVer, buz.truncado);
    return '<div style="border:1px solid ' + C.linea + ';border-radius:8px;padding:12px 14px;margin-bottom:14px">'
      + '<div style="color:' + C.texto + ';font-size:0.875rem;font-weight:600">El buzon esta vacio, y eso puede ser dos cosas</div>'
      + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:5px;line-height:1.45">'
      + 'La tabla contesto y no trae ninguna decision. Eso puede querer decir que no hay ninguna '
      + 'esperando, o que no se han podido <b>escribir</b>: leer y escribir el buzon son dos permisos '
      + 'distintos y esta pantalla solo usa el de leer.<br>'
      + 'Lo de abajo sale de las tareas, que es la otra fuente y todavia tiene las suyas.</div></div>';
  };

  const pintar = () => {
    const marcas = vistas();
    const total = res.ok ? res.filas.length : 0;

    if (!total) {
      // UN VACIO MUDO SE LEE COMO "esta rota". Se dice POR QUE esta vacio y
      // CUANTO se miro para poder decirlo.
      $cuerpo.innerHTML = pintarBuzon()
        + '<div style="padding:26px;text-align:center;border:1px solid ' + C.linea + ';border-radius:8px">'
        + '<div style="font-size:1.5rem;margin-bottom:8px">&#9989;</div>'
        + '<div style="color:' + C.texto + ';font-size:.9rem">Nada espera tu decision.</div>'
        + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:6px">'
        + (res.ok
          ? 'Se revisaron ' + res.leidas + ' tareas' + (res.truncado ? ' (y el barrido se corto: puede haber mas)' : '') + '.'
          : 'Las tareas NO se pudieron leer (' + esc(res.detalle || res.motivo || 'sin motivo') + '), asi que este vacio '
            + 'no dice que no haya: dice que no se miro.')
        + '</div></div>';
      return;
    }

    const visibles = mostrarTodas ? res.filas : res.filas.slice(0, CUANTAS);
    const ocultas = total - visibles.length;

    $cuerpo.innerHTML = pintarBuzon()
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-bottom:10px">'
      + '<strong style="color:' + C.texto + '">' + total + '</strong> esperan tu decision'
      + (ocultas > 0 ? ', y aqui van las ' + visibles.length + ' primeras' : '')
      // EL ORDEN SE DICE COMO ES, y ahora es MIXTO de verdad. Yo habia escrito
      // que la cola no podia ordenar por costo. Se puede, para las que lo traen
      // escrito; las demas van despues por prioridad. Y se dice CUANTAS son de
      // cada clase: un orden a medias presentado como completo es peor que no
      // ordenar, porque el que mira cree que lo de arriba es lo mas urgente.
      + (res.conNivel
        ? '. Ordenadas por <strong>lo que cuesta no decidirlas</strong> en las ' + res.conNivel
          + ' que lo traen escrito, y por prioridad y antiguedad en las otras ' + (total - res.conNivel) + '.'
        : '. Ordenadas por <strong>prioridad y antiguedad</strong>: ninguna trae escrito lo que cuesta no decidirla.')
      + '</div>'
      + visibles.map((f) => tarjetaHtml(f, marcas[f.id])).join('')
      + (ocultas > 0
        ? '<button data-cola-mas style="padding:6px 12px;border-radius:6px;border:1px solid ' + C.linea + ';'
          + 'background:var(--bg-tertiary);color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Ver las otras ' + ocultas + '</button>'
        : (mostrarTodas && total > CUANTAS
          ? '<button data-cola-menos style="padding:6px 12px;border-radius:6px;border:1px solid ' + C.linea + ';'
            + 'background:var(--bg-tertiary);color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Ver solo las ' + CUANTAS + ' primeras</button>'
          : ''))
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:12px;opacity:.85">'
      + 'Se revisaron ' + res.leidas + ' tareas en ' + res.paginas + ' pagina' + (res.paginas === 1 ? '' : 's')
      + (res.filtroDelServidor ? ' (la API filtro por compuerta)' : ' (barrido completo: la API todavia no filtra por compuerta)')
      + (res.truncado ? ' &middot; <strong style="color:' + C.ojo + '">el barrido se corto por el tope: puede haber mas</strong>' : '')
      + '. Las marcas de "ya la viste" viven en este navegador: no viajan a otro equipo.</div>';
  };

  pintar();

  // Filtros del tablero: se repinta todo (no hay inputs de texto que pierdan el foco).
  $cuerpo.addEventListener('change', (ev) => {
    const $quien = ev.target.closest('[data-dec-quien]');
    if (!$quien) return;
    filtro.pedidaPor = $quien.value;
    cuantasVer = DECISIONES_POR_TANDA;
    pintar();
  });

  $cuerpo.addEventListener('click', async (ev) => {
    const $est = ev.target.closest('[data-dec-estado]');
    if ($est) { filtro.estado = $est.getAttribute('data-dec-estado'); cuantasVer = DECISIONES_POR_TANDA; pintar(); return; }
    if (ev.target.closest('[data-dec-mas]')) { cuantasVer += DECISIONES_POR_TANDA; pintar(); return; }
    if (ev.target.closest('[data-cola-mas]')) { mostrarTodas = true; pintar(); return; }
    if (ev.target.closest('[data-cola-menos]')) { mostrarTodas = false; pintar(); return; }

    const $fila = ev.target.closest('[data-cola-fila]');
    if (!$fila) return;
    const id = $fila.getAttribute('data-cola-fila');
    const fila = res.filas.find((f) => f.id === id);
    if (!fila) return;
    const $aviso = $fila.querySelector('[data-cola-aviso]');
    const decir = (t, color) => { if ($aviso) { $aviso.style.color = color || C.suave; $aviso.textContent = t; } };

    if (ev.target.closest('[data-cola-visto]')) {
      marcarVista(id, $fila.getAttribute('data-version'));
      pintar();
      return;
    }

    const $pub = ev.target.closest('[data-cola-publicar]');
    if (!$pub) return;
    const decision = $fila.querySelector('[data-cola-decision]')?.value || '';
    if (!decision.trim()) { decir('Escribi que decidis antes de publicarlo.', C.ojo); return; }
    $pub.disabled = true;
    decir('Publicando...');
    const r = await JosseColaApiClient.decidir({ tarea: fila, decision });
    $pub.disabled = false;
    if (!r.ok) { decir(r.detalle || 'No se pudo publicar.', C.mal); return; }
    // Se dice QUE quedo escrito, no "listo": el titulo es lo que el agente
    // parado va a leer, y verlo aqui es la unica forma de saber que se publico
    // lo que se queria publicar.
    decir('Publicado: ' + r.titulo, C.bien);
    marcarVista(id, $fila.getAttribute('data-version'));
  });
}
